/// <reference types="google.maps" />
import { useEffect, useMemo, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/loadGoogleMaps";
import { Button } from "@/components/ui/button";

export type RouteStopPoint = {
  id: string;
  customer_name: string;
  address: string;
  lat: number | null;
  lng: number | null;
  /** True when the stop is a previously missed service pulled into the route. */
  catchUp?: boolean;
};

export type Depot = { lat: number; lng: number; address: string };

export type CrewRoute = {
  name: string;
  jobs: RouteStopPoint[];
  start?: Depot | null;
  end?: Depot | null;
};


/** Distinct route colors, cycled per crew. */
const CATCH_UP_COLOR = "#f59e0b";

const COLORS = ["#16a34a", "#2563eb", "#d97706", "#db2777", "#7c3aed", "#0891b2"];

export default function CrewRouteMap({ crews }: { crews: CrewRoute[] }) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<Array<google.maps.Marker | google.maps.Polyline>>([]);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});

  const colorFor = useMemo(() => {
    const m: Record<string, string> = {};
    crews.forEach((c, i) => (m[c.name] = COLORS[i % COLORS.length]));
    return m;
  }, [crews]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((g) => {
        if (cancelled || !boxRef.current) return;
        mapRef.current = new g.maps.Map(boxRef.current, {
          center: { lat: 39.7392, lng: -104.9903 },
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        setReady(true);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Map failed to load"));
    return () => {
      cancelled = true;
    };
  }, []);

  // Redraw markers + polylines whenever routes or visibility change.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const g = (window as any).google as typeof google;

    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];

    const bounds = new g.maps.LatLngBounds();
    let any = false;

    crews.forEach((c) => {
      if (hidden[c.name]) return;
      const color = colorFor[c.name];
      const jobPath: google.maps.LatLngLiteral[] = [];
      let startPos: google.maps.LatLngLiteral | null = null;
      let endPos: google.maps.LatLngLiteral | null = null;

      /** Square depot marker for the crew's start / end address. */
      const addDepot = (d: Depot, label: "Start" | "End") => {
        const pos = { lat: d.lat, lng: d.lng };
        if (label === "Start") startPos = pos;
        else endPos = pos;
        bounds.extend(pos);
        any = true;
        overlaysRef.current.push(
          new g.maps.Marker({
            position: pos,
            map,
            zIndex: 999,
            title: `${c.name} · ${label} — ${d.address}`,
            label: { text: label[0], color: "#ffffff", fontSize: "11px", fontWeight: "700" },
            icon: {
              path: "M -9 -9 L 9 -9 L 9 9 L -9 9 Z",
              scale: 1,
              fillColor: color,
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          })
        );
      };

      if (c.start) addDepot(c.start, "Start");

      c.jobs.forEach((j, idx) => {
        if (j.lat == null || j.lng == null) return;
        const pos = { lat: j.lat, lng: j.lng };
        jobPath.push(pos);
        bounds.extend(pos);
        any = true;
        const marker = new g.maps.Marker({
          position: pos,
          map,
          title: `${c.name} · stop ${idx + 1} — ${j.customer_name}${
            j.catchUp ? " (catch-up: missed service)" : ""
          }\n${j.address}`,
          label: { text: String(idx + 1), color: "#ffffff", fontSize: "11px", fontWeight: "700" },
          zIndex: j.catchUp ? 500 : undefined,
          icon: j.catchUp
            ? {
                // Diamond outlined in amber marks a missed service catch-up stop.
                path: "M 0 -14 L 14 0 L 0 14 L -14 0 Z",
                scale: 1,
                fillColor: color,
                fillOpacity: 1,
                strokeColor: CATCH_UP_COLOR,
                strokeWeight: 3,
                labelOrigin: new g.maps.Point(0, 0),
              }
            : {
                path: g.maps.SymbolPath.CIRCLE,
                scale: 11,
                fillColor: color,
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 2,
              },
        });
        overlaysRef.current.push(marker);
      });

      const end = c.end ?? c.start;
      if (end) addDepot(end, "End");

      // Solid line between consecutive jobs.
      if (jobPath.length > 1) {
        overlaysRef.current.push(
          new g.maps.Polyline({
            path: jobPath,
            map,
            strokeColor: color,
            strokeOpacity: 0.85,
            strokeWeight: 3,
            icons: [
              {
                icon: { path: g.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.5 },
                offset: "50%",
                repeat: "120px",
              },
            ],
          })
        );
      }

      // Dotted legs to/from the depots.
      const dashed = (from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral) =>
        overlaysRef.current.push(
          new g.maps.Polyline({
            path: [from, to],
            map,
            strokeColor: color,
            strokeOpacity: 0,
            strokeWeight: 3,
            icons: [
              {
                icon: {
                  path: g.maps.SymbolPath.CIRCLE,
                  scale: 2.5,
                  fillColor: color,
                  fillOpacity: 0.9,
                  strokeOpacity: 0,
                },
                offset: "0",
                repeat: "12px",
              },
            ],
          })
        );

      if (startPos && jobPath.length) dashed(startPos, jobPath[0]);
      if (endPos && jobPath.length) dashed(jobPath[jobPath.length - 1], endPos);
      if (startPos && endPos && !jobPath.length) dashed(startPos, endPos);


    });

    if (any) map.fitBounds(bounds, 48);
  }, [crews, hidden, colorFor, ready]);

  const missing = crews.reduce(
    (n, c) => n + c.jobs.filter((j) => j.lat == null || j.lng == null).length,
    0
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">Route map</h3>
        {crews.map((c) => (
          <Button
            key={c.name}
            size="sm"
            variant="outline"
            className="h-6 gap-1.5 px-2 text-xs"
            onClick={() => setHidden((h) => ({ ...h, [c.name]: !h[c.name] }))}
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: colorFor[c.name], opacity: hidden[c.name] ? 0.25 : 1 }}
            />
            <span className={hidden[c.name] ? "text-muted-foreground line-through" : ""}>
              {c.name} ({c.jobs.length})
            </span>
          </Button>
        ))}
        {crews.some((c) => c.jobs.some((j) => j.catchUp)) && (
          <span className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
            <span
              className="inline-block h-2.5 w-2.5 rotate-45 border-2"
              style={{ borderColor: CATCH_UP_COLOR }}
            />
            Catch-up (missed service)
          </span>
        )}
      </div>
      {error ? (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
          {error}
        </p>
      ) : (
        <div ref={boxRef} className="h-[220px] w-full overflow-hidden rounded border bg-muted sm:h-[300px] lg:h-[360px]" />
      )}
      {missing > 0 && (
        <p className="text-xs text-muted-foreground">
          {missing} stop{missing === 1 ? "" : "s"} missing coordinates — optimize the route to
          geocode them.
        </p>
      )}
    </div>
  );
}
